/**
 * CommissionSight pricing — value-based, billed monthly on the account's distinct
 * member count (PMPM; per-employee / PEPM is the same math, only the label
 * changes). There are NO volume tiers: each account is priced per the feature
 * plan it needs, carried as a per-account rate in `account_billing.customRateCents`.
 * `DEFAULT_RATE_CENTS` is the published floor / fallback used only when an account
 * has no custom rate set. Rates are in cents and are never published publicly
 * (see the pricing memory + the marketing "don't publish prices" rule).
 */

/**
 * Default + minimum per-member rate, in cents ($0.25 PMPM). A per-account
 * `customRateCents` (set from the feature plan) ALWAYS overrides this when present.
 */
export const DEFAULT_RATE_CENTS = 25;

export interface PriceResult {
  members: number;
  rateCents: number;
  amountCents: number;
}

/**
 * Total monthly charge for `members` distinct members at the account's rate.
 * A per-account `customRateCents` always wins when provided; absent it, the
 * `DEFAULT_RATE_CENTS` floor applies. The rate is never published.
 */
export function priceForMembers(members: number, customRateCents?: number | null): PriceResult {
  const safe = Math.max(0, Math.floor(members));
  const rateCents =
    customRateCents !== undefined && customRateCents !== null && customRateCents >= 0
      ? Math.floor(customRateCents)
      : DEFAULT_RATE_CENTS;
  return { members: safe, rateCents, amountCents: safe * rateCents };
}
